/**
 * Read-path helpers for avatars and logos.
 *
 * Pick the right pre-baked variant by display size; fall back to the legacy
 * URL when variants are null (rows not yet backfilled). Returned URLs are
 * already cache-busted (the upload route bakes ?v={version} into the variant
 * URL on write), so callers can hand them straight to <Image> or <img>.
 *
 * Display-size → variant mapping (DPR=2 ready, so source ≥ 2× CSS):
 *   ≤  24px CSS  →  64.webp    (map markers, inline badges)
 *   ≤  48px CSS  →  96.webp    (cards, lists, popups, friend-request rows)
 *      > 48px    →  256.webp   (profile headers, full-size views)
 *
 * Callers pass the **CSS pixel size** of the slot; this helper picks the
 * variant whose source pixels are sufficient for sharp DPR=2 rendering.
 */

import type { AvatarVariants } from '@/lib/types'

export type VariantKey = '64' | '96' | '256'

export function pickVariantKey(displayCssPx: number): VariantKey {
  if (displayCssPx <= 24) return '64' // 48px source covers DPR=2 at 24 CSS
  if (displayCssPx <= 48) return '96' // 96px source covers DPR=2 at 48 CSS
  return '256'
}

/**
 * Variants from the DB arrive as `Json` (Supabase generated type). Narrow to
 * the AvatarVariants shape and verify the picked size is actually present.
 * Returns null if anything is off — caller falls back to the legacy URL.
 */
function readVariantUrl(variants: unknown, key: VariantKey): string | null {
  if (!variants || typeof variants !== 'object' || Array.isArray(variants)) return null
  const url = (variants as Record<string, unknown>)[key]
  return typeof url === 'string' && url.length > 0 ? url : null
}

export interface AvatarSource {
  avatar_url?: string | null
  avatar_variants?: unknown // accepts AvatarVariants | Json | null
  avatar_version?: number | null
}

export function getAvatarUrl(
  source: AvatarSource | null | undefined,
  displayCssPx: number
): string | null {
  if (!source) return null
  const variantUrl = readVariantUrl(source.avatar_variants, pickVariantKey(displayCssPx))
  if (variantUrl) return variantUrl
  return source.avatar_url ?? null
}

export interface LogoSource {
  logo_url?: string | null
  logo_variants?: unknown
  logo_version?: number | null
}

export function getLogoUrl(
  source: LogoSource | null | undefined,
  displayCssPx: number
): string | null {
  if (!source) return null
  const variantUrl = readVariantUrl(source.logo_variants, pickVariantKey(displayCssPx))
  if (variantUrl) return variantUrl
  return source.logo_url ?? null
}

/** Services that draw initials on a coloured square (the seed and demo orgs use them). */
const GENERATED_INITIALS_HOSTS = new Set(['ui-avatars.com'])

/**
 * True when the URL is an initials placeholder generated by a service, not an
 * image someone uploaded. The Encuentro frame treats it as no logo: a school
 * without a logo shows its name alone, never an initials square.
 */
export function isGeneratedInitialsImage(url: string): boolean {
  try {
    return GENERATED_INITIALS_HOSTS.has(new URL(url).hostname)
  } catch {
    return false
  }
}

/**
 * Re-export so callers can pass already-typed AvatarVariants without re-importing.
 */
export type { AvatarVariants }
